import { useQueryClient } from '@tanstack/react-query';

import { useMutation, useQuery } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { createNewTechnology, editExistedTechnology, getSiteProfile, removeTechnology } from '@api/manageSite';

import { extractQueryParams } from '@utils/common';

import { IRemoveTechnologyDTO } from '@interfaces/cells/dto/cellsDTO';
import { ISiteProfile } from '@interfaces/cells/siteProfile';

import { ESnackbarTypes } from '@enums/common';

import { CREATE_TECHNOLOGY, DELETE_TECHNOLOGY, EDIT_TECHNOLOGY, SITE_PROFILE } from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

interface IUseSiteProfileParams {
	id?: string;
	closeModals?: () => void;
}

export const useSiteProfile = (params?: IUseSiteProfileParams) => {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();

	const { id, closeModals } = params || {};

	const {
		data,
		isLoading: isLoadingProfile,
		isFetching: isFetchingProfile,
	} = useQuery({
		queryKey: [SITE_PROFILE, id],
		queryFn: extractQueryParams(getSiteProfile),
		enabled: [!!id],
		refetchOnWindowFocus: false,
	});

	const {
		mutate: createTechnology,
		isLoading: isLoadingCreateTechnology,
	} = useMutation({
		mutationKey: [CREATE_TECHNOLOGY],
		mutationFn: createNewTechnology,
		onSuccess: async () => {
			if (closeModals) closeModals();

			dispatch(setSnackbar({
				message: responseMessages.createdTechnology,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([SITE_PROFILE]);
		},
	});

	const {
		mutate: editTechnology,
		isLoading: isLoadingEditTechnology,
	} = useMutation({
		mutationKey: [EDIT_TECHNOLOGY],
		mutationFn: editExistedTechnology,
		onSuccess: async () => {
			if (closeModals) closeModals();

			dispatch(setSnackbar({
				message: responseMessages.updatedTechnology,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([SITE_PROFILE]);
		},
	});

	const {
		mutate: deleteTechnology,
		isLoading: isLoadingDeleteTechnology,
	} = useMutation<IRemoveTechnologyDTO>({
		mutationKey: [DELETE_TECHNOLOGY],
		mutationFn: removeTechnology,
		onSuccess: async () => {
			dispatch(setSnackbar({
				message: responseMessages.deleteTechnology,
				type: ESnackbarTypes.success,
			}));

			return queryClient.invalidateQueries([SITE_PROFILE]);
		},
	});

	return {
		profile: data as ISiteProfile || {},
		isLoadingUpdateProfile: isLoadingProfile || isFetchingProfile,
		isLoadingProfile: isLoadingProfile || isLoadingDeleteTechnology,
		isLoadingTechnologyForm: isLoadingCreateTechnology || isLoadingEditTechnology,
		createTechnology,
		editTechnology,
		deleteTechnology,
	};
};
